import { normalizeSectionId, sectionRenderer } from '@theme/section-renderer';
import { onDocumentReady } from '@theme/utilities';

/** Load slightly before the section enters the viewport */
const ROOT_MARGIN = '1500px 0px';
const FALLBACK_MARGIN_PX = 1500;

/**
 * Featured collection ("product-list") with "Load when scrolled into view":
 * replaces the placeholder with full section output from the Section Rendering API.
 */
function loadDeferredProductList(target) {
  if (target.dataset.deferredLoaded === 'true' || target.dataset.deferredLoading === 'true') {
    return Promise.resolve(false);
  }
  target.dataset.deferredLoading = 'true';

  const sectionEl = target.closest('.shopify-section');
  if (!sectionEl?.id) {
    delete target.dataset.deferredLoading;
    return Promise.resolve(false);
  }

  const normalizedId = normalizeSectionId(sectionEl.id);
  const sectionUrl = new URL(window.location.href);
  sectionUrl.searchParams.set('deferred_load', '1');
  return sectionRenderer
    .renderSection(normalizedId, { cache: false, url: sectionUrl })
    .then(() => {
      target.dataset.deferredLoaded = 'true';

      if (sectionEl) {
        sectionEl.dispatchEvent(
          new CustomEvent('shopify:section:load', {
            bubbles: true,
            detail: { sectionId: normalizedId },
          })
        );
        target.dispatchEvent(
          new CustomEvent('product-list:deferred-loaded', {
            bubbles: true,
            detail: { sectionId: normalizedId },
          })
        );
      }

      return true;
    })
    .catch((err) => {
      console.error('[product-list-deferred]', normalizedId, err);
      return false;
    })
    .finally(() => {
      delete target.dataset.deferredLoading;
    });
}

onDocumentReady(() => {
  const nodes = document.querySelectorAll('[data-defer-product-list]');
  if (!nodes.length) return;
  const pending = new Set(nodes);

  const loadAndDelete = (el) => {
    if (!pending.has(el)) return;
    loadDeferredProductList(el).then((loaded) => {
      if (loaded) pending.delete(el);
    });
  };

  const isNearViewport = (el) => {
    const rect = el.getBoundingClientRect();
    return rect.top < window.innerHeight + FALLBACK_MARGIN_PX && rect.bottom > -FALLBACK_MARGIN_PX;
  };

  let fallbackTickScheduled = false;
  const runFallbackCheck = () => {
    fallbackTickScheduled = false;
    for (const el of pending) {
      if (isNearViewport(el)) loadAndDelete(el);
    }
    if (!pending.size) {
      window.removeEventListener('scroll', scheduleFallbackCheck);
      window.removeEventListener('resize', scheduleFallbackCheck);
    }
  };

  const scheduleFallbackCheck = () => {
    if (fallbackTickScheduled) return;
    fallbackTickScheduled = true;
    window.requestAnimationFrame(runFallbackCheck);
  };

  if (!('IntersectionObserver' in window)) {
    for (const el of nodes) {
      loadAndDelete(el);
    }
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer.unobserve(entry.target);
        loadAndDelete(entry.target);
      }
    },
    { rootMargin: ROOT_MARGIN }
  );

  for (const el of nodes) {
    observer.observe(el);
  }

  window.addEventListener('scroll', scheduleFallbackCheck, { passive: true });
  window.addEventListener('resize', scheduleFallbackCheck, { passive: true });
  scheduleFallbackCheck();
});
